"use client";

import React from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SecuredFooter } from "./ui/secured-footer";
import { useKybStatus } from "../hooks/useKybStatus";
import BusinessAccountPreApplication from "./BusinessAccountPreApplication";
import {
  useActiveBusiness,
  useSetActiveBusiness,
} from "../hooks/query/dposQueries";
import { useToast } from "../hooks/use-toast";

import KybStatusBadge from "./ui/kyb-status-badge";
import headsetIcon from "../assets/images/settings/headset_illustration.svg";
import briefcaseIcon from "../assets/images/settings/briefcase_illustration.svg";
import starIcon from "../assets/images/settings/star_decoration.svg";
import dotIcon from "../assets/images/settings/dot_decoration.svg";

function PageGraphics({ variant }) {
  const mainIcon = variant === "advisor" ? headsetIcon : briefcaseIcon;
  const isAdvisor = variant === "advisor";

  return (
    <div className="relative w-[194px] h-[136px] overflow-clip">
      {/* Purple circle decoration */}
      <div
        className="absolute flex items-center justify-center"
        style={{
          inset: isAdvisor
            ? "49.26% 22.16% 13.24% 51.55%"
            : "61.03% 59.28% 1.47% 14.43%",
        }}
      >
        <div className="flex-none rotate-90 size-[51px]">
          <div className="bg-[#f2e9fb] rounded-full size-full" />
        </div>
      </div>
      {/* Star */}
      <div
        className="absolute"
        style={{
          inset: isAdvisor
            ? "9.56% 67.53% 72.79% 20.1%"
            : "12.5% 15.46% 69.85% 72.16%",
        }}
      >
        <div className="absolute inset-[4.43%_4.42%]">
          <img alt="" className="block max-w-none size-full" src={starIcon} />
        </div>
      </div>
      {/* Cyan dot */}
      <div
        className="absolute flex items-center justify-center"
        style={{
          inset: isAdvisor
            ? "19.85% 24.23% 73.53% 71.13%"
            : "8.09% 69.59% 85.29% 25.77%",
        }}
      >
        <div className="flex-none rotate-90 size-[9px]">
          <img
            alt=""
            className="absolute block max-w-none size-full"
            src={dotIcon}
          />
        </div>
      </div>
      {/* Main icon */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-clip size-[104px]">
        <div
          className="absolute"
          style={{
            inset: isAdvisor ? "6.73% 7.55% 7.56% 6.73%" : "7.14%",
          }}
        >
          <div className="absolute inset-[-1.12%]">
            <img alt="" className="block max-w-none size-full" src={mainIcon} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingScreen() {
  return (
    <div className="w-full bg-white rounded-3xl border border-[#ebe9ef] flex flex-col items-center relative min-h-[780px]">
      <div className="flex flex-col items-center justify-center flex-grow w-full max-w-[363px] px-4 py-12 gap-16">
        <div className="flex flex-col items-center gap-12">
          <PageGraphics variant="advisor" />
          <div className="flex flex-col gap-6 text-center">
            <h1 className="text-[32px] font-semibold leading-[40px] text-[#33125f]">
              Application received!
            </h1>
            <p className="text-[20px] font-normal leading-[32px] text-[#423E51]">
              Thanks for showing interest in applying for a business account.
              We&apos;ll be reaching out to you to discuss the details.
            </p>
          </div>
        </div>
        <KybStatusBadge status="pending" />
      </div>
      <SecuredFooter className="w-full p-6 justify-center border-t border-[#ebe9ef] mt-auto" />
    </div>
  );
}

function ApprovedScreen() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeBusinessId, businesses, refetch, isFetching } =
    useActiveBusiness();
  const setActiveBusinessMutation = useSetActiveBusiness();

  const handleViewBusinessSuite = async () => {
    if (isFetching || setActiveBusinessMutation.isPending) {
      return;
    }

    try {
      const shouldRefreshBusinessProfiles =
        !activeBusinessId || businesses.length === 0;
      const refreshedProfiles = shouldRefreshBusinessProfiles
        ? await refetch()
        : null;
      const nextBusinesses = refreshedProfiles?.data?.businesses || businesses;
      const nextActiveBusinessId =
        refreshedProfiles?.data?.activeBusinessId || activeBusinessId;

      if (!nextActiveBusinessId) {
        const singleBusinessId =
          nextBusinesses.length === 1
            ? nextBusinesses[0]?.businessId || null
            : null;

        if (nextBusinesses.length === 0) {
          toast({
            description: "No business profile is available yet.",
            variant: "destructive",
          });
          return;
        }

        if (singleBusinessId) {
          await setActiveBusinessMutation.mutateAsync(singleBusinessId);
        } else {
          navigate("/trading/digitalWallet");
          return;
        }
      }

      navigate("/trading/business-suite");
    } catch (error) {
      toast({
        description:
          error instanceof Error
            ? error.message
            : "Could not open Business Suite",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full bg-white rounded-3xl border border-[#ebe9ef] flex flex-col items-center relative min-h-[780px]">
      <div className="flex flex-col items-center justify-center flex-grow w-full max-w-[363px] px-4 py-12 gap-16">
        <div className="flex flex-col items-center gap-12">
          <PageGraphics variant="briefcase" />
          <div className="flex flex-col gap-6 text-center">
            <h1 className="text-[32px] font-semibold leading-[40px] text-[#33125f]">
              Business account active.
            </h1>
            <p className="text-[20px] font-normal leading-[32px] text-[#423E51]">
              Thanks for showing interest in applying for a business account.
              We&apos;re happy to let you know your application has been
              approved.
            </p>
          </div>
        </div>
        <KybStatusBadge status="approved" />
        <button
          onClick={() => void handleViewBusinessSuite()}
          disabled={setActiveBusinessMutation.isPending || isFetching}
          className="w-full h-12 rounded-full text-white text-sm font-semibold leading-5 cursor-pointer hover:opacity-90 transition-opacity disabled:cursor-wait disabled:opacity-80 flex items-center justify-center gap-2"
          style={{
            backgroundImage:
              "linear-gradient(201deg, #EB3366 13.24%, #9747FF 130.13%)",
          }}
        >
          {setActiveBusinessMutation.isPending || isFetching ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Opening Business Suite...
            </>
          ) : (
            "View business suite"
          )}
        </button>
      </div>
      <SecuredFooter className="w-full p-6 justify-center border-t border-[#ebe9ef] mt-auto" />
    </div>
  );
}

export default function BusinessAccountStatus() {
  const { status, isLoading } = useKybStatus();

  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-3xl border border-[#ebe9ef] flex items-center justify-center min-h-[780px]">
        <div className="animate-pulse flex flex-col items-center gap-6">
          <div className="w-[194px] h-[136px] bg-gray-100 rounded-2xl" />
          <div className="w-[280px] h-8 bg-gray-100 rounded-lg" />
          <div className="w-[240px] h-6 bg-gray-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (status === "approved") {
    return <ApprovedScreen />;
  }

  if (status === "pending") {
    return <PendingScreen />;
  }

  if (status === "error") {
    return (
      <div className="w-full bg-white rounded-3xl border border-[#ebe9ef] flex flex-col items-center justify-center min-h-[780px] gap-4 px-4">
        <p className="text-[20px] font-semibold text-[#33125f]">
          Unable to load status
        </p>
        <p className="text-[16px] text-[#6d6584] text-center">
          We couldn&apos;t retrieve your application status. Please try again
          later.
        </p>
      </div>
    );
  }

  return <BusinessAccountPreApplication />;
}
